import React from 'react';
import Header from 'components/ui/Header';
import Footer from 'components/ui/Footer';
import Icon from 'components/AppIcon';
import { Link } from 'react-router-dom';

const DigitalProjectManagementServicesPage = () => {
  const projectModules = [
    {
      icon: 'Zap',
      title: 'Élaboration et conduite de projets numériques',
      description: 'Maîtrisez toutes les phases du cycle de vie d\'un projet numérique, de la conception à la livraison finale.',
      features: [
        'Cadrage et définition du périmètre projet',
        'Planification stratégique et opérationnelle',
        'Conduite et pilotage de projet de bout en bout',
        'Gestion des livrables et validation qualité',
        'Clôture de projet et capitalisation'
      ]
    },
    {
      icon: 'DollarSign',
      title: 'Pilotage des ressources, budgets et délais',
      description: 'Optimisez la gestion de vos ressources humaines, financières et temporelles pour garantir la réussite de vos projets.',
      features: [
        'Allocation et optimisation des ressources',
        'Élaboration et suivi budgétaire rigoureux',
        'Planning et gestion des délais critiques',
        'Tableaux de bord et reporting avancé',
        'Gestion des écarts et actions correctives'
      ]
    },
    {
      icon: 'GitBranch',
      title: 'Méthodes agiles : Scrum et Kanban',
      description: 'Adoptez les frameworks agiles les plus performants pour une livraison itérative, flexible et orientée valeur client.',
      features: [
        'Scrum : rôles, artefacts et cérémonies',
        'Product Owner et Scrum Master certifiés',
        'Kanban : flux de travail et amélioration continue',
        'Gestion du backlog et priorisation par la valeur',
        'Sprints, rétrospectives et adaptation'
      ]
    },
    {
      icon: 'Brain',
      title: 'Pilotage de projets d\'Intelligence Artificielle',
      description: 'Gérez avec succès vos projets d\'IA de la conception à la mise en production, avec une approche méthodologique éprouvée.',
      features: [
        'Cadrage et étude de faisabilité projets IA',
        'Gestion des données et modèles Machine Learning',
        'Déploiement, MLOps et industrialisation',
        'Éthique, gouvernance et conformité IA',
        'Mesure de la performance et ROI IA'
      ]
    },
    {
      icon: 'Laptop',
      title: 'Transformation IT et projets de digitalisation',
      description: 'Conduisez la transformation digitale de votre organisation avec une méthodologie structurée et une expertise terrain.',
      features: [
        'Stratégie de transformation digitale globale',
        'Conduite du changement et adoption utilisateurs',
        'Architecture IT et infrastructure cloud',
        'Sécurité, conformité et gouvernance',
        'Migration de systèmes et intégration'
      ]
    },
    {
      icon: 'Target',
      title: 'Pilotage par la valeur et ROI',
      description: 'Maximisez le retour sur investissement de vos projets avec une approche orientée business value et création de valeur.',
      features: [
        'Business case et analyse de rentabilité',
        'Définition et suivi des KPI stratégiques',
        'Mesure du ROI et de la valeur créée',
        'Gestion des risques et opportunités business',
        'Reporting exécutif et tableaux de bord décisionnels'
      ]
    }
  ];

  const whyChooseUs = [
    {
      icon: 'CheckCircle',
      title: 'Livraison Garantie',
      description: '98% de nos projets sont livrés dans les délais et budgets convenus grâce à notre méthodologie éprouvée.'
    },
    {
      icon: 'TrendingUp',
      title: 'Réduction des Coûts',
      description: 'Économies moyennes de 35% sur les coûts de gestion de projet grâce à l\'optimisation des ressources.'
    },
    {
      icon: 'Users',
      title: 'Experts Certifiés',
      description: 'Chefs de projet certifiés PMP, Scrum Master, Prince2, SAFe avec expérience terrain internationale.'
    },
    {
      icon: 'Shield',
      title: 'Gestion des Risques',
      description: 'Approche proactive pour anticiper, évaluer et mitiger les risques projet à chaque phase.'
    },
    {
      icon: 'Award',
      title: 'Méthodologies Éprouvées',
      description: 'Combinaison des meilleures pratiques agiles, traditionnelles et hybrides adaptées à votre contexte.'
    },
    {
      icon: 'BarChart',
      title: 'ROI Mesurable',
      description: 'Suivi rigoureux de la valeur créée et du retour sur investissement avec KPI et tableaux de bord.'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Section 1: Hero avec Introduction */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-20 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `
              linear-gradient(rgba(155, 142, 87, 0.3) 1px, transparent 1px),
              linear-gradient(90deg, rgba(155, 142, 87, 0.3) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-6">
              <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2">
                <Icon name="Settings" size={20} style={{ color: '#9B8E57' }} />
                <span className="text-sm font-semibold" style={{ color: '#9B8E57' }}>
                  Domaine d'Expertise
                </span>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold leading-tight">
                Management de Projets Numériques
              </h1>
              
              <p className="text-xl text-gray-300 leading-relaxed">
                Formations complètes en gestion de projets numériques : méthodes agiles, pilotage de ressources, projets IA, transformation IT et pilotage par la valeur. Maîtrisez toutes les dimensions du management de projet digital.
              </p>

              <div className="grid grid-cols-2 gap-4 py-4">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <div className="text-3xl font-bold" style={{ color: '#9B8E57' }}>6</div>
                  <div className="text-sm text-gray-300">Modules de Formation</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <div className="text-3xl font-bold" style={{ color: '#9B8E57' }}>98%</div>
                  <div className="text-sm text-gray-300">Taux de Réussite</div>
                </div>
              </div>

              <div className="flex flex-wrap gap-4">
                <Link 
                  to="/contact" 
                  className="inline-flex items-center px-8 py-4 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg"
                  style={{ background: 'linear-gradient(135deg, #96121D 0%, #9B8E57 100%)', color: 'white' }}
                >
                  <Icon name="MessageCircle" size={20} className="mr-2" />
                  Consultation Gratuite
                </Link>
                <Link 
                  to="/service-request-form" 
                  className="inline-flex items-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-xl font-semibold hover:bg-white/20 transition-all duration-300 border border-white/20"
                >
                  <Icon name="FileText" size={20} className="mr-2" />
                  Demander un Devis
                </Link>
              </div>
            </div>

            {/* Right Visual */}
            <div className="relative">
              <div className="bg-gradient-to-br from-[#96121D]/20 via-[#9B8E57]/20 to-[#96121D]/10 rounded-3xl p-12 backdrop-blur-sm border border-white/10">
                <Icon name="Laptop" size={200} style={{ color: '#9B8E57' }} className="mx-auto" />
              </div>
              <div 
                className="absolute -bottom-6 -right-6 px-6 py-4 rounded-2xl shadow-2xl"
                style={{ background: 'linear-gradient(135deg, #96121D 0%, #9B8E57 100%)' }}
              >
                <div className="flex items-center space-x-2">
                  <Icon name="CheckCircle" size={24} className="text-white" />
                  <span className="text-sm font-semibold text-white">Certification Reconnue</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Modules de Formation */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Nos Modules de Gestion de Projet</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Formations complètes pour maîtriser tous les aspects du management de projets numériques et digitaux
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projectModules?.map((module, index) => (
              <div 
                key={index} 
                className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group"
              >
                <div 
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300"
                  style={{ background: 'linear-gradient(135deg, #96121D 0%, #9B8E57 100%)' }}
                >
                  <Icon name={module?.icon} size={32} className="text-white" />
                </div>
                
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {module?.title}
                </h3>
                
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {module?.description}
                </p>
                
                <ul className="space-y-3">
                  {module?.features?.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <Icon name="CheckCircle" size={20} style={{ color: '#9B8E57' }} className="mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 3: Pourquoi Nous Choisir */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Pourquoi Choisir CRF Perfection ?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Une expertise en gestion de projet reconnue pour garantir votre succès et maximiser votre ROI
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs?.map((benefit, index) => (
              <div 
                key={index} 
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 text-center group"
              >
                <div 
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300"
                  style={{ background: 'linear-gradient(135deg, #96121D 0%, #9B8E57 100%)' }}
                >
                  <Icon name={benefit?.icon} size={28} className="text-white" />
                </div>
                
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {benefit?.title}
                </h3>
                
                <p className="text-gray-600 leading-relaxed">
                  {benefit?.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 4: CTA */}
      <section 
        className="py-20 text-white relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #96121D 0%, #9B8E57 100%)' }}
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `
              linear-gradient(rgba(255, 255, 255, 0.3) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255, 255, 255, 0.3) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }} />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Prêt à Garantir le Succès de Vos Projets Numériques ?
          </h2>
          <p className="text-xl mb-8 text-white/90">
            Contactez-nous pour une consultation gratuite et découvrez comment nos formations peuvent transformer votre gestion de projet
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact" 
              className="inline-flex items-center justify-center px-8 py-4 bg-white rounded-xl font-semibold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-lg"
              style={{ color: '#96121D' }}
            >
              <Icon name="Phone" size={20} className="mr-2" />
              Contactez-nous
            </Link>
            <Link 
              to="/service-request-form" 
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-xl font-semibold hover:bg-white/20 transition-all duration-300 border-2 border-white"
            >
              <Icon name="FileText" size={20} className="mr-2" />
              Demander un Devis
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DigitalProjectManagementServicesPage;